import { useEffect, useState } from "react";
import { getMirrorAttributeTargetEntities } from "../../../API/Entity/Entity";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import InnerLoader from "../../Shared/Loader/InnerLoader";
import { useTranslation } from "react-i18next";
import { filterBy } from "@progress/kendo-data-query";

function MirrorField({
    entityId,
    entity, relation, attribute,
    onEntityChange, onRelationChange, onAttributeChange,
    attributes,
    disabled = false
}) {

    const { t } = useTranslation("attribute/mirror_field", { useSuspense: false });

    const [loading, setLoading] = useState(!disabled);
    const [entities, setEntities] = useState([]);

    const [selectedEntityData, setSelectedEntityData] = useState({});

    const [filteredEntities, setFilteredEntities] = useState([]);
    const [filteredAttributes, setFilteredAttributes] = useState([]);
    const [filteredRelations, setFilteredRelations] = useState([]);

    useEffect(() => {
        if (disabled) {
            setSelectedEntityData({
                attributes: [],
                relations: []
            })
        } else {
            getMirrorAttributeTargetEntities(entityId)
                .then(entities => {
                    const filteredEntities = checkForStateFieldInEntity(entities);
                    const sortedEntities = sortEntities(filteredEntities);
                    setEntities(sortedEntities);
                    setFilteredEntities(sortedEntities);
                })
                .finally(() => setLoading(false));
        }
    }, [])

    function checkForStateFieldInEntity(entities) {
        const isStateAttributeExist = attributes.find(attr =>
            attr.attributeType === "State Field" ||
            (attr.attributeType === "Mirror" && attr.attribute.attributeType === "State Field")
        );
        if (isStateAttributeExist) {
            return entities.map(entity => {
                const filteredAttributes = entity.attributes.filter(attr =>
                    attr.attributeType !== "Entity Lookup" &&
                    (
                        attr.attributeType !== "State Field" ||
                        (attr.attributeType === "Mirror" && attr.attribute.attributeType !== "State Field")
                    )
                );
                return {
                    ...entity,
                    attributes: filteredAttributes
                };
            });
        } else {
            return entities;
        }
    }

    useEffect(() => {
        if (selectedEntityData && selectedEntityData._id) {
            const sortedAttributes = sortAttributes(selectedEntityData.attributes);
            setFilteredAttributes(sortedAttributes);
            const sortedRelations = sortRelations(selectedEntityData.relations);
            setFilteredRelations(sortedRelations);
        }
    }, [selectedEntityData])

    useEffect(() => {
        if (!disabled && entity && entity._id) {
            const entityData = entities.find(ent => ent._id === entity._id);
            setSelectedEntityData(entityData);
        }
    }, [entity, entities])

    function handleEntityChange(e) {
        onEntityChange(e);
    }

    function handleRelationChange(e) {
        onRelationChange(e);
    }

    function handleAttributeChange(e) {
        onAttributeChange(e);
    }

    function showRelationsDropdown() {
        const entitiesPresent = entities.length > 0;
        const isEntityValid = typeof selectedEntityData === "object" && typeof selectedEntityData._id === "string";
        const hasRelations = isEntityValid && Array.isArray(selectedEntityData.relations) && selectedEntityData.relations.length > 0;
        return disabled ? true : entitiesPresent && isEntityValid && hasRelations;
    }

    function showAttributesDropdown() {
        const relationsPresent = showRelationsDropdown();
        const isRelationValid = typeof selectedEntityData === "object" && typeof selectedEntityData._id === "string";
        const hasAttributes = isRelationValid && Array.isArray(selectedEntityData.attributes) && selectedEntityData.attributes.length > 0;
        return disabled ? true : relationsPresent && isRelationValid && hasAttributes;
    }

    function getSourceObjectProperties() {
        const isStateAttributeExist = attributes.find(attr =>
            attr.attributeType === "State Field" ||
            (attr.attributeType === "Mirror" && attr.attribute.attributeType === "State Field")
        );
        if (isStateAttributeExist) {
            return selectedEntityData.attributes.filter(attr =>
                attr.attributeType !== "State Field" ||
                (attr.attributeType === "Mirror" && attr.attribute.attributeType !== "State Field")
            );
        }
        return selectedEntityData.attributes;
    }

    function handleEntityFilterChange(e) {
        let data = filterBy(entities, e.filter);
        data = sortEntities(data);
        setFilteredEntities(data);
    }

    function handleRelationsFilterChange(e) {
        let data = filterBy(selectedEntityData.relations, e.filter);
        data = sortRelations(data);
        setFilteredRelations(data);
    }

    function handleAttributeFilterChange(e) {
        let data = filterBy(getSourceObjectProperties(), e.filter);
        data = sortAttributes(data);
        setFilteredAttributes(data);
    }

    function sortEntities(entities) {
        return entities.sort((a, b) => a.name.localeCompare(b.name));
    }

    function sortRelations(relations) {
        return relations.sort((a, b) => a.identifier.localeCompare(b.identifier));
    }

    function sortAttributes(attributes) {
        const systemAttributes = attributes.filter(attr => attr.isSystemAttribute);
        const nonSystemAttributes = attributes.filter(attr => !attr.isSystemAttribute && attr.attributeType !== "Image");
        return [
            ...nonSystemAttributes.sort((a, b) => a.attributeName.localeCompare(b.attributeName)),
            ...systemAttributes,
        ]
    }

    if (loading) {
        return <InnerLoader borderColor="green" />
    }

    return (
        <div>
            {
                !disabled && entities.length === 0 ?
                    <span className="text-danger">
                        {t("no_linked_objects_found")}
                    </span>
                    :
                    <div>
                        <label className="form-label" id="mirror_field_source_object_field">
                            {t("source_object")}
                        </label>
                        <ComboBox
                            disabled={disabled}
                            name="entity"
                            ariaLabelledBy="mirror_field_source_object_field"
                            data={filteredEntities}
                            value={entity}
                            textField="name"
                            dataItemKey="_id"
                            onChange={handleEntityChange}
                            filterable={true}
                            onFilterChange={handleEntityFilterChange}
                        />
                    </div>
            }
            {
                showRelationsDropdown()
                    ?
                    <div>
                        <label className="form-label" id="mirror_field_relation_field">
                            {t("relation")}
                        </label>
                        <ComboBox
                            disabled={disabled}
                            name="relation"
                            ariaLabelledBy="mirror_field_relation_field"
                            data={filteredRelations}
                            value={relation}
                            textField="identifier"
                            dataItemKey="_id"
                            onChange={handleRelationChange}
                            filterable={true}
                            onFilterChange={handleRelationsFilterChange}
                        />
                    </div>
                    :
                    <span className="text-danger">
                        {
                            selectedEntityData && selectedEntityData.relations && selectedEntityData.relations.length === 0 ?
                                t("no_relations_found") : ""
                        }
                    </span>
            }
            {
                showAttributesDropdown() ?
                    <div>
                        <label className="form-label" id="mirror_field_attribute_field">
                            {t("source_property")}
                        </label>
                        <ComboBox
                            disabled={disabled}
                            name="attribute"
                            ariaLabelledBy="mirror_field_attribute_field"
                            data={filteredAttributes}
                            value={attribute}
                            textField="attributeName"
                            dataItemKey="_id"
                            filterable={true}
                            onChange={handleAttributeChange}
                            onFilterChange={handleAttributeFilterChange}
                        />
                    </div>
                    :
                    <span className="text-danger">
                        {
                            relation && relation.attributes && relation.attributes.length === 0 ?
                                t("no_properties_found") : ""
                        }
                    </span>
            }
        </div>
    )
}

export default MirrorField;